import React from "react";
import clsx from "clsx";
import { Box, Typography } from "@material-ui/core";
import { InView } from "react-intersection-observer";
import { RouteComponentProps, useHistory } from "react-router-dom";
import dayjs from "dayjs";
import {
  CorpSessionInfo,
  CreditDetail,
  StatementCreditDetail,
  StatementCreditDetailStatus,
  VIEWED_COMP_NIGHT_BANNER,
  VIEWED_CREDIT_BENEFITS_MODAL,
  VIEWED_PREMIUM_OFFER_PAGE,
  ViewedCompNightBannerEntryPoint,
  ViewedCompNightBannerProperties,
  ViewedPremiumOfferPageProperties,
} from "redmond";
import {
  TravelCreditDetailsCard,
  useDeviceTypes,
  ActionLink,
  CurrencyFormatters,
  IconName,
  AnnualTravelCreditInfoBanner,
  BannerSeverity,
  NotificationBanner,
  AnnualTravelCreditInfoModal,
  getATCProductName,
  CREDIT_HOW_DESCRIPTION,
  CREDIT_HOW_TITLE,
  CREDIT_STACKING_ADDITIONAL_DESCRIPTION,
  DefaultImageBanner,
  getRestrictedUserTCTexts,
  getIsCreditAnnual,
  GENERIC_HOTEL_PROMOTION_OFFER_IMAGE,
} from "halifax";
import {
  appendIsTravelAppParam,
  COMPLIMENTARY_NIGHT_PROMOTION_BUTTON_TEXT,
  COMPLIMENTARY_NIGHT_PROMOTION_DESCRIPTION,
  COMPLIMENTARY_NIGHT_PROMOTION_TITLE,
  PATH_PREMIUM_OFFERS_SHOP,
  useIsTravelApp,
} from "@capone/common";
import {
  AVAILABLE,
  LUXURY_COMPLIMENTARY_NIGHT_PROMOTION_ENTRYPOINTS,
  PREMIER_COLLECTION_EXPERIMENT,
} from "@capone/experiments";
import { CreditsConnectorProps } from "./container";
import "./styles.scss";
import { ClientContext } from "../../../../App";
import {
  CREDIT_BALANCE_APPLICABILITY_TEXT,
  CREDIT_BALANCE_DESCRIPTION,
  CREDIT_BALANCE_TEXT,
  CREDIT_DETAILS_DESCRIPTION,
  CREDIT_DETAILS_TITLE,
  READ_TERMS_AND_CONDITIONS,
  VIEW_CREDIT_HISTORY_DESCRIPTION,
  VIEW_ACTIVITY_CTA,
  CREDIT_HISTORY_MODAL_SUBHEADER,
  CREDIT_HISTORY_MODAL_LIST_HEADER,
  CREDIT_HISTORY_BALANCE_TEXT,
} from "../../textConstants";
import { PATH_TERMS_OF_SERVICE } from "../../../../utils/paths";
import { TravelCreditBalanceCard } from "../TravelCreditBalanceCard";
import { trackEvent } from "../../../../api/v1/analytics/trackEvent";
import { config } from "../../../../api/config";
import {
  getExperimentVariant,
  useExperiments,
} from "../../../../context/experiments";

export interface ICreditsProps
  extends CreditsConnectorProps,
    RouteComponentProps {
  isTravelCreditHistoryExperiment?: boolean;
  isCreditAndOfferStackingExperimentV1?: boolean;
  isAnnualTravelCreditsExperiment?: boolean;
  isSpendAndGetOfferEnabled?: boolean;
}

const TRAVEL_CREDIT_TERMS_AND_CONDITIONS_LINK = () => (
  <ActionLink
    className="travel-wallet-terms-and-conditions"
    onClick={() => window.open(PATH_TERMS_OF_SERVICE, "_blank")?.focus()}
    content={READ_TERMS_AND_CONDITIONS}
  />
);

export const Credits = ({
  credit,
  creditBreakdown,
  rewardsAccounts,
  creditTransactionHistory,
  isTravelCreditHistoryExperiment,
  isCreditAndOfferStackingExperimentV1,
  isAnnualTravelCreditsExperiment,
  isSpendAndGetOfferEnabled,
}: ICreditsProps) => {
  const { sessionInfo } = React.useContext(ClientContext);
  const firstName = sessionInfo?.userInfo?.firstName || "";
  const { matchesMobile } = useDeviceTypes();
  const { isTravelApp } = useIsTravelApp();
  const [atcInfoModalOpen, setAtcInfoModalOpen] =
    React.useState<boolean>(false);
  const [ftuxRTCModalOpen, setFTUXRTCModalOpen] =
    React.useState<boolean>(false);
  const [selectedRestrictedCredit, setSelectedRestrictedCredit] =
    React.useState<undefined | CreditDetail>(undefined);
  const history = useHistory();
  const expState = useExperiments();
  const showComplimentaryNightPromotionEntrypoints =
    getExperimentVariant(
      expState.experiments,
      LUXURY_COMPLIMENTARY_NIGHT_PROMOTION_ENTRYPOINTS,
    ) === AVAILABLE;

  const showsPremierCollection =
    getExperimentVariant(
      expState.experiments,
      PREMIER_COLLECTION_EXPERIMENT,
    ) === AVAILABLE;

  const isNonFinancialUser =
    (sessionInfo as CorpSessionInfo)?.corporateInfo?.cap1Role === "NFU";

  const restrictedUserTCTexts = getRestrictedUserTCTexts(
    rewardsAccounts,
    creditBreakdown,
  );

  const expiringTCTexts = React.useMemo(() => {
    if (!isAnnualTravelCreditsExperiment) return undefined;

    const expiringTCs = creditBreakdown.filter(
      (creditDetail) =>
        creditDetail.CreditDetail === "Statement" &&
        creditDetail.status.Status !== StatementCreditDetailStatus.Empty &&
        dayjs().isBefore(creditDetail.expiresOn) &&
        dayjs(creditDetail.expiresOn).diff(dayjs(), "days") < 60,
    ) as StatementCreditDetail[];

    const getTravelCreditCopy = (creditDetail: StatementCreditDetail) => {
      const copy = (
        creditDetail?.copyProperties?.restrictedOnlyCopies as {
          [key: string]: string;
        }
      )?.descriptorCopy;
      return copy ? copy[0].toUpperCase() + copy.slice(1) : "travel credit";
    };

    if (!expiringTCs.length) return undefined;
    return expiringTCs.map((expiringTC) => (
      <>
        {getATCProductName(
          expiringTC?.productType,
          expiringTC?.creditType,
          expiringTC?.transactionType
        )}...
        {expiringTC?.last4}{" "}
        {!getIsCreditAnnual(expiringTC) ||
        expiringTC?.restrictedToProducts?.values.length
          ? getTravelCreditCopy(expiringTC)
          : "annual travel credit"}{" "}
        expires{" "}
        <strong>{dayjs(expiringTC?.expiresOn).format("MMM D, YYYY")}</strong>
      </>
    ));
  }, [creditBreakdown, rewardsAccounts, isAnnualTravelCreditsExperiment]);

  const hasAnnualVentureX = creditBreakdown.some(
    (creditDetail) =>
      creditDetail.CreditDetail === "Statement" &&
      creditDetail.productType === "VentureX" &&
      getIsCreditAnnual(creditDetail) &&
      creditDetail.status.Status !== StatementCreditDetailStatus.Empty,
  );

  const hasAnnualVentureXBusiness = creditBreakdown.some(
    (creditDetail) =>
      creditDetail.CreditDetail === "Statement" &&
      creditDetail.productType === "VentureXBusiness" &&
      getIsCreditAnnual(creditDetail) &&
      creditDetail.status.Status !== StatementCreditDetailStatus.Empty,
  );

  const renderDesktop = () => (
    <Box className={clsx("credits-container", { "spend-and-get-enabled": isSpendAndGetOfferEnabled })}>
      <TravelCreditDetailsCard
        isMobile={matchesMobile}
        title={CREDIT_DETAILS_TITLE}
        description={CREDIT_DETAILS_DESCRIPTION(config.TENANT)}
        rewardsAccounts={rewardsAccounts}
        cardContent={{
          balanceCardProps: {
            description: CREDIT_BALANCE_DESCRIPTION(firstName),
            balanceText: CREDIT_BALANCE_TEXT(credit?.amount),
            applicabilityText: CREDIT_BALANCE_APPLICABILITY_TEXT,
            rewardsAccounts,
            credit,
            creditBreakdown,
          },
          accordionContents: [
            {
              title: CREDIT_HOW_TITLE,
              body: (
                <Box className="credits-how">
                  <Typography className="credits-how-description">
                    {CREDIT_HOW_DESCRIPTION(config.TENANT, isNonFinancialUser)}
                  </Typography>
                  {isCreditAndOfferStackingExperimentV1 && (
                    <Typography className="credit-stacking-description">
                      {CREDIT_STACKING_ADDITIONAL_DESCRIPTION(
                        config.TENANT,
                        isNonFinancialUser,
                      )}
                    </Typography>
                  )}
                </Box>
              ),
            },
          ],
          terms: TRAVEL_CREDIT_TERMS_AND_CONDITIONS_LINK(),
          showCreditHistory:
            isTravelCreditHistoryExperiment &&
            !!creditTransactionHistory.length,
          travelCreditHistoryProps:
            isTravelCreditHistoryExperiment && !!creditTransactionHistory.length
              ? {
                  modalContentProps: {
                    header: CREDIT_BALANCE_DESCRIPTION(firstName),
                    balanceText: CREDIT_HISTORY_BALANCE_TEXT(
                      CurrencyFormatters.getSymbol(credit?.amount.currency),
                      CurrencyFormatters.get(credit?.amount.currency).format(
                        (credit?.amount?.amount &&
                          credit?.amount?.amount * -1) ||
                          0,
                      ),
                    ),
                    subHeader: CREDIT_HISTORY_MODAL_SUBHEADER,
                    creditListHeader: CREDIT_HISTORY_MODAL_LIST_HEADER,
                  },
                  icon: IconName.PiggyBankCircleOutline,
                  viewCreditHistoryDescription: VIEW_CREDIT_HISTORY_DESCRIPTION,
                  viewHistoryCTAText: VIEW_ACTIVITY_CTA,
                  creditTransactionsHistory: creditTransactionHistory,
                }
              : undefined,
        }}
        firstName={firstName}
        restrictedUserTCTexts={restrictedUserTCTexts}
        isAnnualTravelCreditsExperiment={isAnnualTravelCreditsExperiment}
      />
    </Box>
  );

  const renderMobile = () => (
    <Box className="mobile-credits-container">
      {!restrictedUserTCTexts &&
        expiringTCTexts?.length &&
        expiringTCTexts.map((expiringTCText, idx) => (
          <NotificationBanner
            content={expiringTCText}
            severity={BannerSeverity.WARNING}
            className="atc-expiration-banner"
            key={`atc-expiration-banner-${idx + 1}`}
          />
        ))}

      <AnnualTravelCreditInfoBanner
        variant="small"
        rewardsAccounts={rewardsAccounts}
        creditBreakdown={creditBreakdown}
        firstName={firstName}
        onLearnMoreClick={() => {
          trackEvent({
            eventName: VIEWED_CREDIT_BENEFITS_MODAL,
            properties: {},
          });
          setAtcInfoModalOpen(true);
        }}
        hasCreditTransactionHistory={!!creditTransactionHistory.length}
        onHowItWorksClick={(restrictedCredit) => {
          setSelectedRestrictedCredit(restrictedCredit);
          setFTUXRTCModalOpen(true);
        }}
      />
      <AnnualTravelCreditInfoModal
        hasVentureX={hasAnnualVentureX}
        hasVentureXBusiness={hasAnnualVentureXBusiness}
        open={
          atcInfoModalOpen || (selectedRestrictedCredit && ftuxRTCModalOpen)
        }
        setOpen={
          ftuxRTCModalOpen
            ? (open: boolean) => {
                setFTUXRTCModalOpen(open);
                setSelectedRestrictedCredit(undefined);
              }
            : setAtcInfoModalOpen
        }
        isMobile
        credit={selectedRestrictedCredit}
      />

      {!!restrictedUserTCTexts?.length &&
        restrictedUserTCTexts.map((restrictedUserTCText, idx) => (
          <NotificationBanner
            content={restrictedUserTCText}
            severity={BannerSeverity.NOTICE}
            className="restricted-atc-banner"
            key={`restricted-atc-banne-${idx + 1}`}
          />
        ))}
      <TravelCreditBalanceCard
        isTravelCreditHistoryExperiment={isTravelCreditHistoryExperiment}
        isAnnualTravelCreditsExperiment={isAnnualTravelCreditsExperiment}
      />

      {showComplimentaryNightPromotionEntrypoints && (
        <InView
          as="div"
          triggerOnce
          className="mobile-wallet-complimentary-night-promotion-wrapper"
          onChange={(inView) => {
            if (inView) {
                trackEvent({
                    eventName: VIEWED_COMP_NIGHT_BANNER,
                    properties: {
                        step: ViewedCompNightBannerEntryPoint.MY_TRAVEL_CREDITS_OFFERS,
                    } as ViewedCompNightBannerProperties,
                });
            }
          }}
        >
          <DefaultImageBanner
            imageUrl={GENERIC_HOTEL_PROMOTION_OFFER_IMAGE}
            title={COMPLIMENTARY_NIGHT_PROMOTION_TITLE}
            description={COMPLIMENTARY_NIGHT_PROMOTION_DESCRIPTION(
              showsPremierCollection,
            )}
            buttonText={COMPLIMENTARY_NIGHT_PROMOTION_BUTTON_TEXT}
            buttonAction={() => {
              trackEvent({
                eventName: VIEWED_PREMIUM_OFFER_PAGE,
                properties: {
                  entry_point:
                    ViewedCompNightBannerEntryPoint.MY_TRAVEL_CREDITS_OFFERS,
                } as ViewedPremiumOfferPageProperties,
              });
              history.push(
                appendIsTravelAppParam(PATH_PREMIUM_OFFERS_SHOP, isTravelApp),
              );
            }}
          />
        </InView>
      )}

      <Box className="credits-how">
        <Typography className="credits-how-title" variant="h6">
          {CREDIT_HOW_TITLE}
        </Typography>
        <Typography className="credits-how-description">
          {CREDIT_HOW_DESCRIPTION(config.TENANT, isNonFinancialUser)}
        </Typography>
        {isCreditAndOfferStackingExperimentV1 && (
          <Typography className="credit-stacking-description">
            {CREDIT_STACKING_ADDITIONAL_DESCRIPTION(
              config.TENANT,
              isNonFinancialUser,
            )}
          </Typography>
        )}
        <ActionLink
          className="credits-terms-and-conditions"
          onClick={() => {
            if (matchesMobile) {
              history.push(
                `${PATH_TERMS_OF_SERVICE}${
                  isTravelApp ? "?isTravelApp=true" : ""
                }`,
              );
            } else {
              window.open(PATH_TERMS_OF_SERVICE, "_blank")?.focus();
            }
          }}
          content={READ_TERMS_AND_CONDITIONS}
        />
      </Box>
    </Box>
  );

  return <>{matchesMobile ? renderMobile() : renderDesktop()}</>;
};
